import React, { useState, useRef } from 'react';
import { ROLE_LABELS, type AccountDto } from '../../shared/types/index.js';
import { ApiError } from '../api/client.js';
import { useTranslation } from '../i18n/useTranslation.js';
import { useSession } from '../session/SessionProvider.js';

/** The MFA step is still a fixed demo code; only the credentials are verified by the server. */
const DEMO_MFA_CODE = '472916';

const SUCCESS_SCREEN_MS = 900;
const ENTER_DELAY_MS = 2200;

function LoginScreen() {
  const { t } = useTranslation();
  const { login } = useSession();
  const [username, setUsername] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [mfa, setMfa] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  /** The signed-in account, shown on the success screen while the dashboard prepares. */
  const [welcome, setWelcome] = useState<AccountDto | null>(null);
  const [error, setError] = useState('');
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  function handleMfaChange(val: string, idx: number) {
    const next = [...mfa];
    next[idx] = val.slice(-1);
    setMfa(next);
    setError('');
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  }

  function handleMfaKey(e: React.KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key === 'Backspace' && !mfa[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  }

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 401) return t('login.errorInvalidCredentials');
      if (err.status === 403) return t('login.errorSuspended');
      return t('login.errorServer', { message: err.message });
    }
    return t('login.errorServer', { message: (err as Error)?.message ?? String(err) });
  }

  async function handleAuth() {
    if (loading) return;

    if (!username.trim() || !passphrase.trim()) {
      setError(t('login.errorRequired'));
      return;
    }

    if (mfa.join('') !== DEMO_MFA_CODE) {
      setError(t('login.errorInvalidMfa'));
      return;
    }

    setError('');
    setLoading(true);
    try {
      // The success screen plays while the session activation is delayed; then the dashboard takes over.
      await login(username.trim(), passphrase, {
        onAuthenticated: (account) => setTimeout(() => setWelcome(account), SUCCESS_SCREEN_MS),
        activateAfterMs: ENTER_DELAY_MS,
      });
    } catch (err) {
      setError(describeError(err));
      setLoading(false);
    }
  }

  return (
    <div className="login-overlay">
      <div className="login-bg">
        <div className="login-orb1" />
        <div className="login-orb2" />
        <div className="login-scan" />
      </div>
      <div className="login-card">
        {welcome && (
          <div className="login-success-overlay show">
            <div className="login-success-icon">✓</div>
            <div className="login-success-text">{t('login.authSuccess')}</div>
            <div className="login-success-sub">
              {t('login.welcomeMessage', { name: welcome.displayName, role: t(`roles.${welcome.role}`) || ROLE_LABELS[welcome.role] })}
            </div>
          </div>
        )}
        <div className="login-logo">✡</div>
        <div className="login-title">{t('login.title')}</div>
        <div className="login-subtitle">{t('login.subtitle')}</div>

        <div style={{ marginBottom: 12, marginTop: 18 }}>
          <div className="lf-label">{t('login.username')}</div>
          <input
            className="lf-input"
            value={username}
            autoComplete="username"
            onChange={(e) => {
              setUsername(e.target.value);
              setError('');
            }}
            style={{ marginBottom: 10 }}
            onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
          />
          <div className="lf-label">{t('login.passphrase')}</div>
          <input
            className="lf-input"
            type="password"
            value={passphrase}
            autoComplete="current-password"
            onChange={(e) => {
              setPassphrase(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
          />
        </div>

        <div className="lf-label">{t('login.mfaToken')}</div>
        <div className="mfa-row">
          {mfa.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              className="mfa-digit"
              type="text"
              maxLength={1}
              value={d}
              onChange={(e) => handleMfaChange(e.target.value, i)}
              onKeyDown={(e) => handleMfaKey(e, i)}
            />
          ))}
        </div>

        {error && <div className="login-error">{error}</div>}

        <button className="login-btn" disabled={loading} onClick={handleAuth}>
          {loading ? t('login.authenticating') : t('login.authenticate')}
        </button>
        <div className="login-class-badge">{t('login.restrictedAccess')}</div>
      </div>
    </div>
  );
}

export default LoginScreen;
